<template>
  <teleport to="body">
    <div
      class="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
      @click.self="$emit('close')"
    >
      <div class="bg-white rounded-lg shadow-lg max-h-[85vh] flex flex-col" :class="showFeedbackPanel ? 'w-[90%] max-w-6xl' : 'w-3/4 max-w-4xl'">
        <!-- Header -->
        <div class="px-5 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
          <h3 class="text-lg font-medium text-gray-900 truncate pr-4">{{ job.query }}</h3>
          <div class="flex items-center gap-2 shrink-0">
            <!-- Toggle feedback panel -->
            <button
              v-if="isCompleted"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors"
              :class="showFeedbackPanel
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'"
              @click="showFeedbackPanel = !showFeedbackPanel"
            >
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4z" />
              </svg>
              Feedback & Notes
              <span v-if="feedbackCount > 0" class="bg-gray-200 text-gray-700 rounded-full px-1.5 py-0.5 text-xs leading-none" :class="showFeedbackPanel ? 'bg-gray-600 text-white' : ''">{{ feedbackCount }}</span>
            </button>
            <button
              class="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              @click="$emit('close')"
            >&times;</button>
          </div>
        </div>

        <!-- Body: two-pane layout -->
        <div class="flex flex-1 overflow-hidden">
          <!-- Left: scrollable report -->
          <div class="flex-1 p-5 overflow-y-auto" :class="showFeedbackPanel ? 'border-r border-gray-200' : ''">
            <AnalystResult
              ref="analystResult"
              :job="job"
              :polling="polling"
              :regenerating="regenerating"
              @follow-up="handleFollowUp"
              @annotate="handleAnnotate"
              @regenerate="$emit('regenerate')"
            />
          </div>

          <!-- Right: feedback sidecar -->
          <div
            v-if="showFeedbackPanel && isCompleted"
            class="w-[380px] min-w-0 shrink-0 flex flex-col overflow-hidden bg-gray-50/50"
          >
            <div class="p-4 flex-1 overflow-y-scroll overflow-x-hidden">
              <AnalystFeedback
                ref="feedbackComponent"
                :job-id="job.id"
                :feedback="job.feedback || []"
                @feedback-changed="handleFeedbackChanged"
                @navigate-to-anchor="handleNavigateToAnchor"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import AnalystResult from './AnalystResult.vue'
import AnalystFeedback from './AnalystFeedback.vue'

export default {
  name: 'AnalystResultModal',
  components: { AnalystResult, AnalystFeedback },
  props: {
    job: {
      type: Object,
      required: true,
    },
    polling: {
      type: Boolean,
      default: false,
    },
    regenerating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'follow-up', 'feedback-changed', 'regenerate'],
  data() {
    return {
      showFeedbackPanel: true,
      localFeedbackCount: null,
    }
  },
  computed: {
    isCompleted() {
      return this.job.status === 'COMPLETED'
    },
    feedbackCount() {
      if (this.localFeedbackCount !== null) return this.localFeedbackCount
      return this.job.feedback?.length || 0
    },
  },
  methods: {
    handleFollowUp(suggestion) {
      this.$emit('follow-up', suggestion)
      this.$emit('close')
    },
    handleAnnotate({ text, offset, rating }) {
      this.showFeedbackPanel = true
      this.$nextTick(() => {
        if (this.$refs.feedbackComponent) {
          this.$refs.feedbackComponent.setAnchor(text, offset)
          this.$refs.feedbackComponent.selectMode(rating)
        }
      })
    },
    handleNavigateToAnchor({ text }) {
      if (this.$refs.analystResult) {
        this.$refs.analystResult.highlightAnchor(text)
      }
    },
    handleFeedbackChanged(feedbackItems) {
      this.localFeedbackCount = feedbackItems.length
      this.$emit('feedback-changed', feedbackItems)
    },
  },
}
</script>
